import {
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  Button,
  MessageBarIntent,
  Link,
  makeStyles,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useContext, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { DeveloperPortalRootContext, loadBuildConfig, useSelector } from '@tdp';

import {
  ITopBanner,
  notificationSlice,
  NotificationType,
  TopBannerContentType,
} from './slice';
import { HostType } from '../host';
import { settingsSlice } from '../settings';
import { ErrorInformations } from './ErrorInformation';

const IntentMap = new Map<NotificationType, MessageBarIntent>([
  [NotificationType.Success, 'success'],
  [NotificationType.Error, 'error'],
  [NotificationType.Warning, 'warning'],
  [NotificationType.Informational, 'info'],
]);

const useStyles = makeStyles({
  correlationText: {
    paddingLeft: '1rem',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    marginTop: '20px',
  },
});

const MAX_CHARACTERS = 500;

const Notifications: React.FC = () => {
  const notification = useSelector(
    notificationSlice.selectors.currentNotification
  );
  const topBanner = useSelector(notificationSlice.selectors.currentTopBanner);
  const classes = useStyles();
  const dispatch = useDispatch();

  const truncatedContent =
    notification?.message && notification.message.length > MAX_CHARACTERS
      ? notification.message.slice(0, MAX_CHARACTERS) + '...'
      : notification?.message;

  const autoDismissTime = useMemo(() => {
    switch (notification?.type) {
      case NotificationType.Success:
        return 5000; // 5 seconds for success notifications
      case NotificationType.Error:
        return 15000; // 15 seconds for error notifications
      case NotificationType.Warning:
        return 12000; // 12 seconds for warning notifications
      case NotificationType.Informational:
        return 6000; // 6 seconds for informational notifications
      default:
        return 5000; // Default to 5 seconds
    }
  }, [notification?.type]);

  // Auto-dismiss notification if autoDismiss is true
  useEffect(() => {
    if (notification?.show && notification.autoDismiss) {
      const timer = setTimeout(() => {
        handleNotificationDismiss();
      }, notification.dismissAfter ?? autoDismissTime);
      return () => { clearTimeout(timer); };
    }
  }, [notification, notification?.show, notification?.autoDismiss]);

  // Event Handlers
  const handleNotificationDismiss = () => {
    dispatch(notificationSlice.actions.dismissNotification());
  };

  const handleTopBannerDismiss = () => {
    dispatch(notificationSlice.actions.dismissTopBanner());
  };


  return (
    <>
      {topBanner?.show && (
        <MessageBar
          style={{
            width: '100%',
            position: 'relative',
          }}
          intent={IntentMap.get(NotificationType.Informational)}
          role="alert"
          data-testid="banner-bar"
          id="top-banner-message-bar"
          aria-live='assertive'
        >
          <TopBannerBody topBannerNotification={topBanner} />
          <MessageBarActions
            containerAction={
              <Button
                aria-label={'close'}
                aria-describedby="top-banner-message-bar"
                aria-live="off"
                onClick={handleTopBannerDismiss}
                appearance="transparent"
                icon={<DismissRegular />}
                autoFocus
              />
            }
          ></MessageBarActions>
        </MessageBar>
      )}
      {notification?.show && (
        <MessageBar
          style={{
            width: '100%',
            position: 'relative',
          }}
          intent={IntentMap.get(notification.type)}
          role="alert"
          data-testid="notification"
          id="notification-message-bar"
        >
          <MessageBarBody>
            {notification.errorContext?.errorCode
              ? <FormattedMessage
                defaultMessage='Something went wrong'
                description={'Something went wrong generic error message'}
              />
              : truncatedContent}
            {notification.externalLink && (
              <Link
                className={classes.correlationText}
                target="_blank"
                href={notification.externalLink}
              >
                <FormattedMessage
                  defaultMessage='learn more'
                  description={'learn more link text for notification'}
                />
              </Link>
            )}
            <ErrorInformations
              errorContext={notification.errorContext}
              className={classes.correlationText}
              notificationType={notification.type}
            />
          </MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button
                aria-label={'close'}
                aria-describedby="notification-message-bar"
                aria-live="off"
                onClick={handleNotificationDismiss}
                appearance="transparent"
                icon={<DismissRegular />}
                autoFocus
              />
            }
          ></MessageBarActions>
        </MessageBar>
      )}
    </>
  );
};

export default Notifications;

interface TopBannerBodyProps {
  topBannerNotification: ITopBanner | undefined;
}
const TopBannerBody: React.FC<TopBannerBodyProps> = ({ topBannerNotification }) => {
  const intl = useIntl();
  const appRunContext = useSelector(settingsSlice.selectors.appRunContext);
  const { services: { logger } } = useContext(DeveloperPortalRootContext);
  const buildConfig = loadBuildConfig();
  const baseUrl = (buildConfig.api as { baseUrl: string }).baseUrl;
  let clientRedirectionUrl =
    'https://dev-int.teams.cloud.dev.microsoft/?client-preference=classic';
  if (baseUrl === 'https://dev.teams.microsoft.com/api') {
    clientRedirectionUrl =
      'https://dev.teams.cloud.microsoft/?client-preference=classic';
  }

  const onClicKhandler = () => {
    logger.logUserBi({
      telemetryMarker: 'user-action-app-list-banner-switch-to-old-client-triggered',
      message: `From app list banner, user opted to switch to old client: ${clientRedirectionUrl}, from host: ${appRunContext?.host}`,
    });
    if (appRunContext?.host === HostType.TEAMS) {
      window.open(clientRedirectionUrl, '_blank');
    } else {
      window.location.href = clientRedirectionUrl;
    }
  };

  switch (topBannerNotification?.type) {
    case TopBannerContentType.AppListEnvironment:
    default:
      return (
        <MessageBarBody>
          {intl.formatMessage({
            defaultMessage:
              "You are currently using the new Developer Portal. To access apps containing configured environments, switch to the previous version.",
            description: 'Top Banner message for client switch',
          })}
          {' '}
          {(
            <Link
              target="_blank"
              onClick={onClicKhandler}
            >
              {appRunContext?.host === HostType.TEAMS ?
                intl.formatMessage({
                  defaultMessage: 'Go to previous version',
                  description: 'Top Banner button text for client switch',
                }) :
                intl.formatMessage({
                  defaultMessage: 'Switch to previous version',
                  description: 'Top Banner button text for client switch',
                })}
            </Link>
          )}
        </MessageBarBody>
      );
  }
};
